import React, { useEffect, useState } from "react";
import { Box, Divider, Grid, CircularProgress } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import PatientDatas from "src/modules/medcard/patient-datas";
import { useAppSelector } from "src/app/hooks";
import { mcGeneralDiseaseService, McGeneralDiseaseItem } from "src/api/medcards/mcGeneralDiseaseService";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import { useTranslate } from "src/locales";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { parseISO } from "date-fns";

interface FamilyHistoryModalProps {
  open: boolean;
  onClose: () => void;
  patientId?: string | number;
  editData?: McGeneralDiseaseItem | null;
}

const FamilyHistoryModal: React.FC<FamilyHistoryModalProps> = ({
  open,
  onClose,
  patientId,
  editData,
}) => {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => (state as any).auth);
  const selectedOfficeId = useAppSelector((state) => (state as any).auth.selectedOfficeId);
  const selectedOffice = user?.UserAssPriviliges?.find((office: any) => office.ID === selectedOfficeId);
  const offid = Number(selectedOffice?.OFF_ID || user?.UserAssPriviliges?.[0]?.OFF_ID || 2);

  const [operationDate, setOperationDate] = useState<Date | null>(new Date());
  const [operationUser, setOperationUser] = useState<string>("");
  const [relation, setRelation] = useState<string>("");
  const [relationName, setRelationName] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [note, setNote] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState(false);

  // Kullanıcı adını otomatik olarak login durumundan al
  useEffect(() => {
    if (user?.username) {
      setOperationUser(user.username);
    }
  }, [user]);

  // Edit data varsa form'u doldur
  useEffect(() => {
    if (open && editData) {
      setRelation(editData.pid?.toString() || "");
      setRelationName(editData.name || "");
      setSelectedItem({
        id: editData.pid,
        name: editData.name,
        displayField: editData.name,
      });
      setNote(editData.note || editData.privatenotes || "");
      if (editData.cuser) {
        setOperationUser(editData.cuser);
      }
      if (editData.ctime) {
        try {
          const date = typeof editData.ctime === 'string' ? parseISO(editData.ctime) : new Date(editData.ctime);
          setOperationDate(date);
        } catch (e) {
          setOperationDate(new Date());
        }
      }
    }
  }, [open, editData]);

  // Modal kapandığında state'leri temizle
  useEffect(() => {
    if (!open) {
      setRelation("");
      setRelationName("");
      setSelectedItem(null);
      setNote("");
      setSnackbarOpen(false);
    }
  }, [open]);

  const handleSave = async () => {
    if (!patientId) {
      setSnackbarMessage(t("family-history.errors.patient-id-not-found"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    if (!relation) {
      setSnackbarMessage(t("family-history.errors.relation-not-selected"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    try {
      if (editData) {
        // Update
        const updateData = {
          id: editData.id,
          note: note || "",
        };
        await mcGeneralDiseaseService.updateFamilyHistory(updateData);
      } else {
        // Add
        const requestData = {
          ptid: Number(patientId),
          processid: Number(relation),
          note: note || "",
        };
        await mcGeneralDiseaseService.addFamilyHistory(requestData);
      }

      setSnackbarMessage(t("family-history.success.save-success"));
      setSnackbarType("success");
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      console.error("Soy Geçmiş kaydetme hatası:", error);
      const errorMessage = error?.response?.data?.error || error?.error || t("family-history.errors.save-error");
      setSnackbarMessage(errorMessage);
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          py: { xs: 2, sm: 3 },
          px: { xs: 1.5, sm: 2 },
          width: "100%",
          maxWidth: "100%",
        }}
      >
        {/* Başlık */}
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#f4f7fe",
            borderBottom: "1px solid #e0e0e0",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "flex-start",
            py: 1.5,
            px: 2.5,
            mb: 2,
          }}
        >
          <ByzMuiTypography isTitle={true} sx={{ fontSize: 20, lineHeight: "22px", color: "#111827" }}>
            {t("family-history.title")}
          </ByzMuiTypography>
        </Box>

        {/* Save / Close */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            gap: 1,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} disabled={loading} />
          <Box sx={{ flex: 1 }} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Hasta bilgisi kartı (grafiksiz) */}
        <Box sx={{ mb: 2 }}>
          <PatientDatas patientId={patientId} isCreateMedCard noMargin />
        </Box>

        {/* Form alanları */}
        <Box
          sx={{
            mt: 1,
            width: { xs: "100%", md: "60%" },
          }}
        >
          {/* Bölüm başlığı - Soygeçmiş */}
          <Box
            sx={{
              backgroundColor: "#F4F7FE",
              borderBottom: "1px solid #E0E0E0",
              mb: 1.5,
              px: 3,
              py: 1,
            }}
          >
            <ByzMuiTypography isTitle={true}>
              {t("family-history.title")}
            </ByzMuiTypography>
          </Box>

          <Grid container spacing={0}>
            {/* İşlem Tarihi */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("family-history.operation-date")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiDateTimePicker
                    value={operationDate}
                    onChange={(val) => setOperationDate(val as Date | null)}
                    width="100%"
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* İşlem Yapan Kullanıcı */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("family-history.operation-user")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="İşlem Yapan Kullanıcı"
                    value={operationUser}
                    onChange={(e) => setOperationUser(e.target.value)}
                    fullWidth
                    cantChange
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Yakınlığı */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("family-history.relation")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzSelectListPopupInput
                    label={t("buttons.chose") || "Seçiniz"}
                    placeholder={t("buttons.chose") || "Seçiniz"}
                    value={relationName}
                    selectedItem={selectedItem}
                    disabled={!!editData}
                    singleSelection={true}
                    onChange={(value: any) => {
                      const item = Array.isArray(value) && value.length > 0 ? value[0] : value;
                      if (item) {
                        setSelectedItem(item);
                        setRelation(String(item?.id || item?.ID || ""));
                        setRelationName(item?.name || item?.NAME || "");
                      } else {
                        setSelectedItem(null);
                        setRelation("");
                        setRelationName("");
                      }
                    }}
                    url="/reference/api/McGeneralDisease/field-family-history"
                    method="POST"
                    postData={{
                      offid: offid,
                    }}
                    dialogTitle={t("family-history.dialog-title")}
                    displayField="name"
                    columns={[
                      { field: "id", headerName: "ID", width: 100 },
                      { field: "name", headerName: t("family-history.relation"), flex: 1 },
                    ]}
                    dialogShowOkButton={true}
                    rowID={patientId}
                    showPatientData={false}
                    fullWidth
                    sx={{ width: "100%" }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Not */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("family-history.note")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="Not"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      <ByzMuiSnackBar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        title={snackbarType === "success" ? t("common.success") : t("common.error")}
        message={snackbarMessage}
        type={snackbarType}
      />
    </OpenComponentDialog>
  );
};

export default FamilyHistoryModal;



